/**
 * Rest-space analysis of an arbitrary imported skeleton.
 *
 * Everything the mapping heuristics, the binding builder and `BoundRig` need to know about a
 * source skeleton is derived here, and it is always derived from the **bind pose** (plan D14) —
 * never from whatever pose the bones happen to be in right now. `skeleton.boneInverses[i]` is
 * the inverse of the bone's world matrix at the moment `SkinnedMesh.bind()` ran, so inverting it
 * gives the bone's rest world matrix directly.
 *
 * Pure domain logic: `three` math only, no DOM, no editor state.
 */

import { Matrix4, Quaternion, Vector3 } from 'three';
import type { Bone, Object3D, SkinnedMesh } from 'three';

import { identityQuat, normalizeQuat, quatInvert, quatMultiply } from '../shared';
import type { QuaternionTuple, Vec3Tuple } from '../shared';

export interface SourceBoneInfo {
  name: string;
  /** Nearest ancestor that is itself a bone, or `null` for a top-level bone. */
  parentName: string | null;
  /** Depth within the bone forest: a bone with no bone ancestor has depth 0. */
  depth: number;
  restWorldPosition: Vec3Tuple;
  restWorldQuaternion: QuaternionTuple;
  /** Rest local rotation: `parentRestWorld^-1 * restWorld` (parent = actual `Object3D` parent). */
  restLocalQuaternion: QuaternionTuple;
  /** Number of bones whose `parentName` is this bone. */
  childCount: number;
}

export interface SkeletonAnalysis {
  bones: SourceBoneInfo[];
  byName: Map<string, SourceBoneInfo>;
  skinnedMeshes: SkinnedMesh[];
}

/** One node's local transform as `captureRestPose` found it. */
export interface NodeTransformSnapshot {
  position: Vec3Tuple;
  quaternion: QuaternionTuple;
}

/**
 * Local position + rotation of every node BELOW a root, keyed by the object itself.
 *
 * Keyed by object rather than by name because it covers NON-bone nodes too and their names are
 * not unique (a GLB whose root motion sits on an `Armature`/group is moved by the mixer as well,
 * and has to be put back). Scale is not captured: nothing in the pipeline animates it.
 */
export type SkeletonPoseSnapshot = Map<Object3D, NodeTransformSnapshot>;

function isBoneObject(o: Object3D): o is Bone {
  return (o as Bone).isBone === true;
}

function isSkinnedMeshObject(o: Object3D): o is SkinnedMesh {
  return (o as SkinnedMesh).isSkinnedMesh === true;
}

/**
 * Every bone under `root` keyed by name, in depth-first order. Duplicate names keep the first
 * occurrence: GLB import is responsible for enforcing unique, sanitize-safe bone names (D17).
 */
export function collectBoneObjects(root: Object3D): Map<string, Object3D> {
  const out = new Map<string, Object3D>();
  root.traverse((o) => {
    if (isBoneObject(o) && !out.has(o.name)) out.set(o.name, o);
  });
  return out;
}

function nearestBoneAncestor(bone: Object3D, boneSet: Set<Object3D>): Object3D | null {
  let p = bone.parent;
  while (p) {
    if (boneSet.has(p)) return p;
    p = p.parent;
  }
  return null;
}

/**
 * Inspect a loaded model and describe its skeleton in rest (bind) space.
 *
 * Only objects with `isBone` are considered bones. Bones that belong to no `SkinnedMesh`
 * skeleton (a lone armature root, for instance) fall back to their current world transform,
 * which for a freshly loaded model is also their rest transform.
 */
export function analyzeSkeleton(root: Object3D): SkeletonAnalysis {
  root.updateMatrixWorld(true);

  const boneObjects: Object3D[] = [];
  const skinnedMeshes: SkinnedMesh[] = [];
  root.traverse((o) => {
    if (isBoneObject(o)) boneObjects.push(o);
    if (isSkinnedMeshObject(o)) skinnedMeshes.push(o);
  });

  const boneSet = new Set<Object3D>(boneObjects);

  // Rest world matrices from the bind pose. First skeleton that mentions a bone wins.
  const restWorld = new Map<Object3D, Matrix4>();
  for (const mesh of skinnedMeshes) {
    const skeleton = mesh.skeleton;
    if (!skeleton) continue;
    const { bones, boneInverses } = skeleton;
    for (let i = 0; i < bones.length; i++) {
      const bone = bones[i];
      const inverse = boneInverses[i];
      if (!bone || !inverse || restWorld.has(bone)) continue;
      restWorld.set(bone, new Matrix4().copy(inverse).invert());
    }
  }
  for (const bone of boneObjects) {
    if (!restWorld.has(bone)) restWorld.set(bone, new Matrix4().copy(bone.matrixWorld));
  }

  const scratchPos = new Vector3();
  const scratchQuat = new Quaternion();
  const scratchScale = new Vector3();

  const worldQuatOf = new Map<Object3D, QuaternionTuple>();
  const positionOf = new Map<Object3D, Vec3Tuple>();

  for (const bone of boneObjects) {
    const matrix = restWorld.get(bone);
    if (!matrix) continue;
    matrix.decompose(scratchPos, scratchQuat, scratchScale);
    worldQuatOf.set(bone, normalizeQuat([scratchQuat.x, scratchQuat.y, scratchQuat.z, scratchQuat.w]));
    positionOf.set(bone, [scratchPos.x, scratchPos.y, scratchPos.z]);
  }

  /** Rest world rotation of any object: bind pose for bones, current world for everything else. */
  function restWorldQuatOf(o: Object3D | null): QuaternionTuple {
    if (!o) return identityQuat();
    const known = worldQuatOf.get(o);
    if (known) return known;
    o.matrixWorld.decompose(scratchPos, scratchQuat, scratchScale);
    return normalizeQuat([scratchQuat.x, scratchQuat.y, scratchQuat.z, scratchQuat.w]);
  }

  const bones: SourceBoneInfo[] = [];
  const byName = new Map<string, SourceBoneInfo>();
  const depthOf = new Map<Object3D, number>();
  const childCounts = new Map<string, number>();

  for (const bone of boneObjects) {
    const parentBone = nearestBoneAncestor(bone, boneSet);
    const depth = parentBone ? (depthOf.get(parentBone) ?? 0) + 1 : 0;
    depthOf.set(bone, depth);

    const ownWorld = restWorldQuatOf(bone);
    const parentWorld = restWorldQuatOf(bone.parent);
    const restLocal = normalizeQuat(quatMultiply(quatInvert(parentWorld), ownWorld));

    const info: SourceBoneInfo = {
      name: bone.name,
      parentName: parentBone ? parentBone.name : null,
      depth,
      restWorldPosition: positionOf.get(bone) ?? [0, 0, 0],
      restWorldQuaternion: ownWorld,
      restLocalQuaternion: restLocal,
      childCount: 0,
    };

    if (info.parentName !== null) {
      childCounts.set(info.parentName, (childCounts.get(info.parentName) ?? 0) + 1);
    }

    bones.push(info);
    if (!byName.has(info.name)) byName.set(info.name, info);
  }

  for (const info of bones) info.childCount = childCounts.get(info.name) ?? 0;

  return { bones, byName, skinnedMeshes };
}

/** True when `childName` sits below `ancestorName` in the source bone hierarchy. */
export function isDescendantOf(
  byName: ReadonlyMap<string, SourceBoneInfo>,
  childName: string,
  ancestorName: string,
): boolean {
  if (childName === ancestorName) return false;
  let cursor = byName.get(childName)?.parentName ?? null;
  const guard = new Set<string>();
  while (cursor) {
    if (cursor === ancestorName) return true;
    if (guard.has(cursor)) return false;
    guard.add(cursor);
    cursor = byName.get(cursor)?.parentName ?? null;
  }
  return false;
}

/**
 * Snapshot the current local position and rotation of every node BELOW `root` — not only the
 * bones. An `AnimationMixer` drives whatever node a track names, and since the clip sampler keeps
 * position tracks (the `hipsY` channel is read off the Hips world height), a group or `Armature`
 * carrying root motion moves during sampling and must be restored along with the bones. Those
 * nodes are all descendants of the file scene, so covering descendants is enough.
 *
 * `root` ITSELF is deliberately excluded. The root belongs to the game, not to the clip: an
 * integrator who moved, turned or scaled the character between capture and restore would
 * otherwise find it teleported back to where a bake happened to start. Nothing in the pipeline
 * animates the bound root either — it is the object the caller handed us, not one the file named.
 */
export function captureRestPose(root: Object3D): SkeletonPoseSnapshot {
  const snapshot: SkeletonPoseSnapshot = new Map();
  root.traverse((o) => {
    if (o === root) return;
    snapshot.set(o, {
      position: [o.position.x, o.position.y, o.position.z],
      quaternion: [o.quaternion.x, o.quaternion.y, o.quaternion.z, o.quaternion.w],
    });
  });
  return snapshot;
}

/** Restore what `captureRestPose` captured. Objects absent from the snapshot stay put. */
export function restoreRestPose(root: Object3D, snapshot: SkeletonPoseSnapshot): void {
  for (const [object, transform] of snapshot) {
    const p = transform.position;
    const q = transform.quaternion;
    object.position.set(p[0], p[1], p[2]);
    object.quaternion.set(q[0], q[1], q[2], q[3]);
  }
  root.updateMatrixWorld(true);
}
